const BASE_URL = '/api';

export const REDIRECT_STORAGE_KEY = 'aeo.redirectAfterLogin';

export class ApiError extends Error {
  status: number;
  payload?: unknown;

  constructor(status: number, message: string, payload?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

// Network and HTTP-success subscriber sets. These let the global
// ConnectionWatcher component count consecutive network errors (DNS,
// refused connection, offline) without each call site having to special-case
// them. A "successful" notification covers any HTTP response that reached
// the server — even a 500 — because that means the server is reachable.
const networkErrorSubscribers = new Set<(err: TypeError) => void>();
const networkSuccessSubscribers = new Set<() => void>();

export function onNetworkError(handler: (err: TypeError) => void): () => void {
  networkErrorSubscribers.add(handler);
  return () => {
    networkErrorSubscribers.delete(handler);
  };
}

export function onNetworkSuccess(handler: () => void): () => void {
  networkSuccessSubscribers.add(handler);
  return () => {
    networkSuccessSubscribers.delete(handler);
  };
}

function notifyNetworkError(err: TypeError): void {
  for (const h of networkErrorSubscribers) {
    try {
      h(err);
    } catch {
      // Subscribers must not throw; swallow defensively.
    }
  }
}

function notifyNetworkSuccess(): void {
  for (const h of networkSuccessSubscribers) {
    try {
      h();
    } catch {
      // Subscribers must not throw; swallow defensively.
    }
  }
}

function rememberRedirectTarget(): void {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname + window.location.search;
  if (path.startsWith('/login')) return;
  try {
    window.sessionStorage.setItem(REDIRECT_STORAGE_KEY, path);
  } catch {
    // Private mode / disabled storage — best-effort only.
  }
}

export async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  // FormData uploads must not carry a hand-set Content-Type, otherwise the
  // multipart boundary is missing and the server can't parse the body.
  const isFormData =
    typeof FormData !== 'undefined' && options?.body instanceof FormData;
  const baseHeaders: Record<string, string> = isFormData
    ? {}
    : { 'Content-Type': 'application/json' };

  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      ...options,
      credentials: 'include',
      headers: {
        ...baseHeaders,
        ...(options?.headers ?? {}),
      },
    });
  } catch (err) {
    if (err instanceof TypeError) {
      notifyNetworkError(err);
      throw new ApiError(0, 'network', null);
    }
    throw err;
  }

  notifyNetworkSuccess();

  if (res.status === 204) {
    return undefined as T;
  }

  if (res.status === 401) {
    rememberRedirectTarget();
  }

  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const body = isJson ? await res.json().catch(() => null) : await res.text();

  if (!res.ok) {
    const message =
      (isJson && body && typeof body === 'object' && 'error' in body && typeof (body as { error: unknown }).error === 'string'
        ? (body as { error: string }).error
        : `API error: ${res.status}`);
    throw new ApiError(res.status, message, body);
  }

  return body as T;
}
